<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const newPassword = ref('')
const confirmPassword = ref('')
const submitError = ref('')
const submitSuccess = ref('')
const isSubmitting = ref(false)
let redirectTimer = null

const resetToken = computed(() => {
  const token = route.query?.token
  return typeof token === 'string' ? token.trim() : ''
})

const hasResetToken = computed(() => Boolean(resetToken.value))

const passwordMismatch = computed(() => {
  if (!confirmPassword.value) return false
  return newPassword.value !== confirmPassword.value
})

const canSubmit = computed(() => {
  return !isSubmitting.value && hasResetToken.value && Boolean(newPassword.value.trim()) && !passwordMismatch.value
})

watch(
  hasResetToken,
  (hasToken) => {
    if (!hasToken) {
      submitError.value = 'Missing reset token. Please request a new password reset link.'
      submitSuccess.value = ''
    }
  },
  { immediate: true },
)

const handleResetPassword = async () => {
  if (isSubmitting.value) return

  submitError.value = ''
  submitSuccess.value = ''

  if (!hasResetToken.value) {
    submitError.value = 'Missing reset token. Please request a new password reset link.'
    return
  }

  if (!newPassword.value.trim()) {
    submitError.value = 'New password is required.'
    return
  }

  if (passwordMismatch.value) {
    submitError.value = 'Passwords do not match.'
    return
  }

  isSubmitting.value = true

  const result = await authStore.resetPassword(resetToken.value, newPassword.value)

  isSubmitting.value = false

  if (!result.success) {
    submitError.value = authStore.error || 'This reset link is invalid or has expired. Please request a new one.'
    newPassword.value = ''
    confirmPassword.value = ''
    return
  }

  submitSuccess.value = 'Password has been reset successfully.'
  newPassword.value = ''
  confirmPassword.value = ''

  if (authStore.isAuthenticated) {
    const userRefresh = await authStore.fetchUser()
    if (!userRefresh.success) {
      submitError.value = userRefresh.error || 'Failed to refresh user session after password change.'
      return
    }

    authStore.log('PASSWORD_CHANGE_SUCCESS_REDIRECT target=/dashboard')
    await router.push('/dashboard')
    return
  }

  submitSuccess.value = 'Password has been reset successfully. Redirecting to login...'

  if (redirectTimer) {
    clearTimeout(redirectTimer)
  }
  redirectTimer = setTimeout(() => {
    router.push('/login')
  }, 1500)
}

onBeforeUnmount(() => {
  if (redirectTimer) {
    clearTimeout(redirectTimer)
  }
})
</script>

<template>
  <div class="reset-password-container">
    <h1>Reset Password</h1>

    <form @submit.prevent="handleResetPassword">
      <div class="form-group">
        <label for="new-password">New Password</label>
        <input
          id="new-password"
          v-model="newPassword"
          type="password"
          required
          autocomplete="new-password"
          :disabled="isSubmitting"
        />
      </div>

      <div class="form-group">
        <label for="confirm-password">Confirm New Password</label>
        <input
          id="confirm-password"
          v-model="confirmPassword"
          type="password"
          required
          autocomplete="new-password"
          :disabled="isSubmitting"
        />
      </div>

      <div v-if="passwordMismatch" class="error">
        Passwords do not match.
      </div>

      <button type="submit" :disabled="!canSubmit">
        {{ isSubmitting ? 'Resetting...' : 'Set New Password' }}
      </button>

      <div v-if="submitError" class="error">
        {{ submitError }}
      </div>

      <div v-if="submitSuccess" class="info">
        {{ submitSuccess }}
      </div>

      <button type="button" class="secondary" :disabled="isSubmitting" @click="router.push('/login')">
        Back to Login
      </button>
    </form>
  </div>
</template>

<style scoped>
.reset-password-container {
  max-width: 400px;
  margin: 100px auto;
  padding: 2rem;
  border: 1px solid #ddd;
  border-radius: 8px;
}

h1 {
  text-align: center;
  color: #111827;
  margin-bottom: 2rem;
}

form {
  display: grid;
  gap: 1rem;
}

.form-group {
  display: grid;
  gap: 0.5rem;
}

label {
  color: #111827;
  font-weight: bold;
}

input[type='password'] {
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
  color: #111827;
}

button {
  padding: 0.75rem;
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
}

button:hover:not(:disabled) {
  background-color: #1976d2;
}

button:disabled {
  background-color: #ccc;
}

.secondary {
  background-color: #6b7280;
}

.secondary:hover:not(:disabled) {
  background-color: #4b5563;
}

.error {
  background-color: #ffebee;
  color: #c62828;
  padding: 0.75rem;
  border-radius: 4px;
}

.info {
  background-color: #e8f5e9;
  color: #1b5e20;
  padding: 0.75rem;
  border-radius: 4px;
}
</style>